import { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Search, MapPin } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { safeConsole } from '../../lib/piiProtection';

interface LabSelectorProps {
  selectedLabId: string;
  onSelectLab: (labId: string) => void;
}

interface Lab {
  id: string;
  name: string;
  isInHouse: boolean;
  phone?: string;
  address?: string;
  city?: string;
}

export function LabSelector({ selectedLabId, onSelectLab }: LabSelectorProps) {
  const { clinic } = useAuth();
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadLabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]);

  const loadLabs = async () => {
    setLoading(true);
    try {
      const response = await api.labs.getLabs(true);
      const data = response.data || [];
      setLabs(data);

      // Auto-select if only one lab available
      if (data.length === 1) {
        onSelectLab(data[0].id);
      }
    } catch (err) {
      safeConsole.error('Failed to load labs:', err);
    } finally {
      setLoading(false);
    }
  };

  const labLocation = (lab: Lab) => [lab.address, lab.city].filter(Boolean).join(', ');

  const filteredLabs = labs.filter((lab) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      lab.name.toLowerCase().includes(term) ||
      labLocation(lab).toLowerCase().includes(term) ||
      (lab.phone || '').toLowerCase().includes(term)
    );
  });

  // Backend already sorts in-house first, then name — but re-sort defensively after client-side
  // filtering so the grouping still holds regardless of search results.
  const sortedLabs = [...filteredLabs].sort((a, b) => {
    if (a.isInHouse && !b.isInHouse) return -1;
    if (!a.isInHouse && b.isInHouse) return 1;
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (labs.length === 0) {
    return (
      <Alert severity="warning">
        No labs registered for your clinic yet. Please contact admin to set up a lab.
      </Alert>
    );
  }

  return (
    <Box>
      {labs.length > 3 && (
        <TextField
          fullWidth
          placeholder="Search by name, area or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />
      )}

      <FormControl fullWidth>
        <InputLabel>Select Lab *</InputLabel>
        <Select
          value={selectedLabId}
          onChange={(e) => onSelectLab(e.target.value)}
          label="Select Lab *"
        >
          {sortedLabs.flatMap((lab, idx) => {
            const items: JSX.Element[] = [];
            const prev = sortedLabs[idx - 1];
            const startsInHouseGroup = lab.isInHouse && (idx === 0 || !prev?.isInHouse);
            const startsOtherGroup = !lab.isInHouse && (idx === 0 || Boolean(prev?.isInHouse));
            if (startsInHouseGroup) {
              items.push(
                <ListSubheader key="header-in-house" sx={{ lineHeight: '32px' }}>
                  In-house
                </ListSubheader>
              );
            } else if (startsOtherGroup) {
              items.push(
                <ListSubheader key="header-other" sx={{ lineHeight: '32px' }}>
                  Other registered labs
                </ListSubheader>
              );
            }
            items.push(
              <MenuItem key={lab.id} value={lab.id}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {lab.name}
                    </Typography>
                    {lab.isInHouse && (
                      <Chip label="In-house" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  {labLocation(lab) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MapPin size={12} />
                      {labLocation(lab)}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
            return items;
          })}
        </Select>
      </FormControl>

      {selectedLabId && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
          {(() => {
            const selected = labs.find((l) => l.id === selectedLabId);
            if (!selected) return null;
            return (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Lab
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {selected.name}
                  </Typography>
                  {selected.isInHouse && (
                    <Chip label="In-house" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
                {labLocation(selected) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <MapPin size={12} />
                    {labLocation(selected)}
                  </Typography>
                )}
                {selected.phone && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Contact: {selected.phone}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
}
